import React, { useState, useEffect } from 'react';
import { SparklesIcon, PlayIcon, ArrowPathIcon, ShareIcon, HeartIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

const QUICK_PROMPTS = [
  "Send me a birthday message",
  "Tell me you're proud of me",
  "Give me some encouragement",
  "Say you love me",
  "Share some life advice",
  "Send holiday wishes",
  "Tell me you miss me",
  "Congratulate me on my achievement"
];

export default function ComeAlive() {
  const { user } = useAuth();
  const [voiceId, setVoiceId] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [portalLink, setPortalLink] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    loadVoiceClone();
    loadPortalLink();
    loadMessageCount();
  }, [user]);

  const loadVoiceClone = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/podcast/progress/${user.id}`);
      console.log('Loaded voice_id:', response.data.session?.voice_id);
      if (response.data.session?.voice_id) {
        setVoiceId(response.data.session.voice_id);
      }
    } catch (error) {
      console.error('No voice clone found');
    }
  };

  const loadPortalLink = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/portal/link/${user.id}`);
      setPortalLink(response.data.portal_url);
    } catch (error) {
      console.log('No portal link yet');
    }
  };

  const loadMessageCount = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/messages/user/${user.id}`);
      setSavedCount(response.data.messages?.length || 0);
    } catch (error) {
      console.log('No messages yet');
    }
  };

  const generateMessage = async () => {
    if (!voiceId) {
      alert('Please complete your podcast to create a voice clone first');
      return;
    }

    if (!prompt.trim()) {
      alert('Please enter what you want to say');
      return;
    }

    console.log('Generating message with voice_id:', voiceId);
    setGenerating(true);
    try {
      if (!user) {
        alert('Please log in to generate messages');
        return;
      }
      
      const response = await axios.post(`${API_URL}/messages/come-alive`, {
        user_id: user.id,
        prompt: prompt,
        voice_id: voiceId
      });

      setCurrentMessage({
        text: response.data.text,
        audio_url: response.data.audio_url
      });
      
    } catch (error) {
      console.error('Failed to generate message:', error);
      alert('Failed to generate message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const saveMessage = async () => {
    if (!currentMessage) return;

    try {
      await axios.post(`${API_URL}/messages/save`, {
        user_id: user?.id || 'demo_user',
        occasion: 'custom',
        occasion_name: 'Custom Message',
        text: currentMessage.text,
        audio_url: currentMessage.audio_url,
        type: 'dynamic'
      });

      alert('Message saved to your portal!');
      setSavedCount(savedCount + 1);
      
    } catch (error) {
      console.error('Failed to save message:', error);
      alert('Failed to save message');
    }
  };

  const copyPortalLink = () => {
    if (portalLink) {
      navigator.clipboard.writeText(`${window.location.origin}${portalLink}`);
      alert('Portal link copied! Share it with your loved ones.');
    }
  };

  if (!voiceId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-8 flex items-center justify-center">
        <div className="max-w-2xl mx-auto">
          <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-12 text-center">
            <HeartIcon className="h-20 w-20 text-pink-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Voice Clone Required</h2>
            <p className="text-purple-200 mb-8 text-lg">
              Complete your podcast recording to create a voice clone, then come back here to bring your voice to life.
            </p>
            <a
              href="/podcast"
              className="inline-block px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 shadow-lg"
            >
              Go to Podcast Studio
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-white/10 backdrop-blur-sm rounded-full mb-4">
            <SparklesIcon className="h-12 w-12 text-yellow-300" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-3">Come Alive</h1>
          <p className="text-xl text-purple-200">Speak to your loved ones in your voice, anytime</p>
        </div>

        {/* Portal Link */}
        {portalLink && savedCount > 0 && (
          <div className="mb-8 bg-emerald-900/30 border border-emerald-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 font-semibold mb-1">
                  {savedCount} message{savedCount !== 1 ? 's' : ''} saved
                </p>
                <p className="text-sm text-emerald-400">Share your portal link with family</p>
              </div>
              <button
                onClick={copyPortalLink}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
              >
                <ShareIcon className="h-5 w-5" />
                Copy Link
              </button>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8 md:p-12">
          
          {!currentMessage ? (
            <>
              {/* Input */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-white mb-4">
                  What do you want to say?
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Example: Send me a birthday message, or tell me you're proud of me..."
                  className="w-full h-32 px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white text-lg placeholder-purple-300 resize-none focus:outline-none focus:border-purple-400"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      generateMessage();
                    }
                  }}
                />
              </div>

              {/* Quick Prompts */}
              <div className="mb-8">
                <p className="text-sm text-purple-300 mb-3">Quick ideas:</p>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_PROMPTS.map((quickPrompt, index) => (
                    <button
                      key={index}
                      onClick={() => setPrompt(quickPrompt)}
                      className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-purple-200 hover:bg-white/10 hover:border-purple-400 transition-all text-left"
                    >
                      {quickPrompt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateMessage}
                disabled={generating || !prompt.trim()}
                className="w-full px-8 py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xl font-bold rounded-2xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-2xl"
              >
                {generating ? (
                  <>
                    <ArrowPathIcon className="h-6 w-6 animate-spin" />
                    Bringing your voice to life...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-6 w-6" />
                    Generate Message
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Generated Message */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white">Your Message</h3>
                  <button
                    onClick={() => {
                      setCurrentMessage(null);
                      setPrompt('');
                    }}
                    className="text-sm text-purple-300 hover:text-white"
                  >
                    ← New Message
                  </button>
                </div>

                <div className="p-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-2xl mb-6">
                  <p className="text-white text-lg italic leading-relaxed">
                    "{currentMessage.text}"
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
                  <p className="text-sm text-purple-300 mb-3">Listen to your message:</p>
                  <audio
                    controls
                    src={`${API_URL}${currentMessage.audio_url}`}
                    className="w-full"
                    autoPlay
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={generateMessage}
                    className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 flex items-center justify-center gap-2"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                    Regenerate
                  </button>
                  <button
                    onClick={saveMessage}
                    className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:from-emerald-700 hover:to-green-700 flex items-center justify-center gap-2"
                  >
                    <HeartIcon className="h-5 w-5" />
                    Save to Portal
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Info */}
        <div className="mt-8 text-center">
          <p className="text-purple-300 text-sm">
            Messages are generated using your voice clone and personality from your podcast
          </p>
        </div>
      </div>
    </div>
  );
}
